const DISCLAIMER_HEADING = 'Important health and nutrition notice';

function friendlyDate(value) {
  if (!value) return '—';
  return new Date(`${value}T00:00:00Z`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

export function planStatus(plan) {
  if (plan?.status === 'superseded' || (plan?.status === 'delivered' && plan.expires_on < new Date().toISOString().slice(0, 10))) return 'Archived';
  if (plan?.status === 'approved' && plan.parent_review_status === 'pending') return 'Waiting for parent review';
  if (plan?.status === 'approved' && plan.parent_review_status === 'approved') return 'Parent approved';
  return ({ draft: 'Draft', needs_player: 'Waiting for player', ready: 'Ready for review', approved: 'Staff approved', delivered: 'Current' })[plan?.status] || plan?.status;
}

export const SUGGESTED_PLAN_FOOTERS = {
  workout: 'This is a suggested workout plan. Adjust or stop any exercise that causes pain, and talk with a parent, coach, athletic trainer, or medical professional when needed.',
  nutrition: 'This is a suggested nutrition plan for general guidance. Follow your known allergies and food restrictions, and talk with a parent or qualified professional when needed.',
};

export default function PlanDocument({ plan, playerName, editable = false, onChange }) {
  const content = plan?.plan_content;
  if (!content) return null;
  const change = (path, value) => {
    if (!onChange) return;
    const next = structuredClone(content);
    let cursor = next;
    for (let i = 0; i < path.length - 1; i += 1) cursor = cursor[path[i]];
    cursor[path[path.length - 1]] = value;
    onChange(next);
  };
  const field = (value, path, className = '') => editable ? (
    <textarea value={value || ''} onChange={(e) => change(path, e.target.value)} className={`input resize-y print:border-0 print:p-0 print:shadow-none ${className}`} />
  ) : value;

  return (
    <article className="plan-print bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <header className="bg-hunter-deep text-white px-6 sm:px-9 py-8">
        <p className="text-mid-green text-xs uppercase tracking-[0.25em] font-bold">Coach Cole Football</p>
        <p className="mt-3 text-sm font-bold uppercase tracking-[0.12em]">Suggested {plan.plan_type === 'nutrition' ? 'Nutrition' : 'Workout'} Plan</p>
        {editable ? field(content.title, ['title'], 'mt-2 text-xl font-black bg-white text-silver-text') : <h1 className="text-3xl font-display font-black mt-2">{content.title}</h1>}
        <p className="mt-2 text-silver-a">Prepared for {playerName}</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 text-sm">
          <div><span className="block text-mid-green text-xs font-bold uppercase">Effective</span>{friendlyDate(plan.start_date)}</div>
          <div><span className="block text-mid-green text-xs font-bold uppercase">Expires</span>{friendlyDate(plan.expires_on)}</div>
          <div><span className="block text-mid-green text-xs font-bold uppercase">Phase</span>{plan.season_phase}</div>
          <div><span className="block text-mid-green text-xs font-bold uppercase">Duration</span>{plan.duration_weeks} weeks</div>
        </div>
      </header>
      <div className="px-6 sm:px-9 py-8 space-y-8">
        <section>
          <h2 className="font-display font-black text-xl text-hunter">Plan overview</h2>
          <div className="mt-2 text-sm leading-7 text-silver-text">{field(content.summary, ['summary'])}</div>
        </section>
        {Array.isArray(content.athlete_snapshot) && content.athlete_snapshot.length > 0 && (
          <section className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-lg bg-silver-a p-4">
            {content.athlete_snapshot.map((item, index) => <div key={index} className="text-sm"><strong className="text-hunter">{item.label}:</strong> {item.value}</div>)}
          </section>
        )}
        {(content.sections || []).map((section, sectionIndex) => (
          <section key={sectionIndex} className="break-inside-avoid">
            <h2 className="font-display font-black text-xl text-hunter border-b-2 border-mid-green pb-2">{section.heading}</h2>
            <div className="mt-3 text-sm leading-6 text-silver-mute">{field(section.intro, ['sections', sectionIndex, 'intro'])}</div>
            <div className="mt-4 space-y-4">
              {(section.items || []).map((item, itemIndex) => (
                <div key={itemIndex} className="rounded-lg border border-gray-200 p-4 break-inside-avoid">
                  <div className="font-bold text-hunter">{editable ? field(item.title, ['sections', sectionIndex, 'items', itemIndex, 'title']) : item.title}</div>
                  <div className="mt-2 space-y-2">
                    {(item.details || []).map((detail, detailIndex) => editable
                      ? <div key={detailIndex}>{field(detail, ['sections', sectionIndex, 'items', itemIndex, 'details', detailIndex])}</div>
                      : <p key={detailIndex} className="text-sm leading-6 text-silver-text">• {detail}</p>)}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
        {Array.isArray(content.safety_notes) && content.safety_notes.length > 0 && (
          <section className="rounded-lg border border-amber-300 bg-amber-50 p-5 break-inside-avoid">
            <h2 className="font-display font-black text-lg text-amber-900">{DISCLAIMER_HEADING}</h2>
            <p className="mt-2 text-xs leading-5 text-amber-900">{content.safety_notes.join(' ')}</p>
          </section>
        )}
        <section className="rounded-lg border border-mid-green/40 bg-sage-100 p-5 break-inside-avoid">
          <h2 className="font-display font-black text-lg text-hunter">About this suggested plan</h2>
          <p className="mt-2 text-sm leading-6 text-silver-text">{SUGGESTED_PLAN_FOOTERS[plan.plan_type]}</p>
        </section>
        <footer className="text-xs text-silver-mute border-t border-gray-200 pt-4">
          {plan.status === 'superseded' || (plan.status === 'delivered' && plan.expires_on < new Date().toISOString().slice(0, 10))
            ? 'Archived suggested plan'
            : plan.status === 'delivered'
            ? `Reviewed by CCF staff and parent/guardian · Delivered ${plan.delivered_at ? new Date(plan.delivered_at).toLocaleDateString() : ''}`
            : plan.status === 'approved' && plan.parent_review_status === 'approved'
              ? 'Parent/guardian approved · Ready for athlete delivery'
              : plan.status === 'approved'
                ? 'CCF staff reviewed · Waiting for parent/guardian review'
                : 'Draft — not yet reviewed'}
        </footer>
      </div>
    </article>
  );
}
